import { proxyCustomElement, HTMLElement, h } from '@stencil/core/internal/client';
import { d as defineCustomElement$6 } from './cosmos-asset-item-base2.js';
import { d as defineCustomElement$5 } from './cosmos-avatar2.js';
import { d as defineCustomElement$4 } from './cosmos-icon-external-link2.js';
import { d as defineCustomElement$3 } from './cosmos-image2.js';
import { d as defineCustomElement$2 } from './cosmos-text2.js';
import { b as bem } from './bem.js';
import { h as hasNamedSlot } from './slot-utils.js';
import { i as isResponsiveProperty, p as parseResponsiveProperty, m as mapResponsiveProperty, s as serializeResponsiveProperty } from './responsivePropertyHelper.js';

const cosmosPersonItemCss = ".cosmos-person-item{--cosmos-asset-item-base-gap:var(--cosmos-person-item-gap);--cosmos-asset-item-base-headline-color:var(--cosmos-person-item-name-color);--cosmos-asset-item-base-headline-font-family:var(--cosmos-person-item-name-font-family);--cosmos-asset-item-base-headline-font-size:var(--cosmos-person-item-name-font-size);--cosmos-asset-item-base-headline-font-style:var(--cosmos-person-item-name-font-style);--cosmos-asset-item-base-headline-font-weight:var(--cosmos-person-item-name-font-weight);--cosmos-asset-item-base-headline-letter-spacing:var(--cosmos-person-item-name-letter-spacing);--cosmos-asset-item-base-headline-line-height:var(--cosmos-person-item-name-line-height);--cosmos-asset-item-base-headline-text-transform:var(--cosmos-person-item-name-text-transform);--cosmos-asset-item-base-meta-text-primary-color:var(--cosmos-person-item-discipline-color);--cosmos-asset-item-base-meta-text-primary-font-family:var(--cosmos-person-item-discipline-font-family);--cosmos-asset-item-base-meta-text-primary-font-size:var(--cosmos-person-item-discipline-font-size);--cosmos-asset-item-base-meta-text-primary-font-style:var(--cosmos-person-item-discipline-font-style);--cosmos-asset-item-base-meta-text-primary-font-weight:var(--cosmos-person-item-discipline-font-weight);--cosmos-asset-item-base-meta-text-primary-letter-spacing:var(--cosmos-person-item-discipline-letter-spacing);--cosmos-asset-item-base-meta-text-primary-line-height:var(--cosmos-person-item-discipline-line-height);--cosmos-asset-item-base-meta-text-primary-text-transform:var(--cosmos-person-item-discipline-text-transform);--cosmos-asset-item-base-meta-text-secondary-color:var(--cosmos-person-item-nationality-color);--cosmos-asset-item-base-meta-text-secondary-font-family:var(--cosmos-person-item-nationality-font-family);--cosmos-asset-item-base-meta-text-secondary-font-size:var(--cosmos-person-item-nationality-font-size);--cosmos-asset-item-base-meta-text-secondary-font-style:var(--cosmos-person-item-nationality-font-style);--cosmos-asset-item-base-meta-text-secondary-font-weight:var(--cosmos-person-item-nationality-font-weight);--cosmos-asset-item-base-meta-text-secondary-letter-spacing:var(--cosmos-person-item-nationality-letter-spacing);--cosmos-asset-item-base-meta-text-secondary-line-height:var(--cosmos-person-item-nationality-line-height);--cosmos-asset-item-base-meta-text-secondary-text-transform:var(--cosmos-person-item-nationality-text-transform);--cosmos-asset-item-base-meta-text-additional-color:var(--cosmos-person-item-additional-info-color);--cosmos-asset-item-base-meta-text-additional-font-family:var(--cosmos-person-item-additional-info-font-family);--cosmos-asset-item-base-meta-text-additional-font-size:var(--cosmos-person-item-additional-info-font-size);--cosmos-asset-item-base-meta-text-additional-font-style:var(--cosmos-person-item-additional-info-font-style);--cosmos-asset-item-base-meta-text-additional-font-weight:var(--cosmos-person-item-additional-info-font-weight);--cosmos-asset-item-base-meta-text-additional-letter-spacing:var(--cosmos-person-item-additional-info-letter-spacing);--cosmos-asset-item-base-meta-text-additional-line-height:var(--cosmos-person-item-additional-info-line-height);--cosmos-asset-item-base-meta-text-additional-text-transform:var(--cosmos-person-item-additional-info-text-transform)}.cosmos-person-item__external-icon-container{--cosmos-icon-size:16px;display:inline;opacity:0.6;vertical-align:middle;white-space:nowrap}.cosmos-person-item__external-icon{-webkit-margin-start:4px;margin-inline-start:4px}.cosmos-person-item__flag{display:-ms-flexbox;display:flex}.cosmos-person-item__flag ::slotted([slot=flag]){--cosmos-flag-width:16px;-webkit-margin-end:4px;margin-inline-end:4px}.cosmos-person-item__additional-info{display:-ms-flexbox;display:flex}.cosmos-person-item__additional-info ::slotted([slot=additional-info-prefix]){-webkit-margin-end:4px;margin-inline-end:4px}.cosmos-person-item__avatar{--cosmos-avatar-border-radius:var(--cosmos-person-item-profile-image-radius, 9999px);height:100%;width:100%}";
const CosmosPersonItemStyle0 = cosmosPersonItemCss;

const generateClassNames = bem('cosmos-person-item');
const CosmosPersonItem$1 = /*@__PURE__*/ proxyCustomElement(class CosmosPersonItem extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.additionalInfo = undefined;
        this.appearance = undefined;
        this.discipline = undefined;
        this.external = undefined;
        this.href = undefined;
        this.name = undefined;
        this.nationality = undefined;
        this.profileImage = undefined;
        this.profileImageFramed = false;
        this.rel = undefined;
        this.size = 'small';
        this.target = undefined;
    }
    hasFlagSlot() {
        return hasNamedSlot(this.element, 'flag');
    }
    hasAdditionalInfoSlot() {
        return hasNamedSlot(this.element, 'additional-info-prefix');
    }
    getSizeClasses() {
        return this.size && isResponsiveProperty(this.size) ? parseResponsiveProperty(this.size) : this.size;
    }
    getInitialsTextSize() {
        const sizeToTextSizeMap = {
            small: 'x-small',
            medium: 'large',
        };
        if (!this.size) {
            return;
        }
        if (isResponsiveProperty(this.size)) {
            const responsiveSize = parseResponsiveProperty(this.size);
            const responsiveTextSize = mapResponsiveProperty(responsiveSize, sizeToTextSizeMap);
            return serializeResponsiveProperty(responsiveTextSize);
        }
        return sizeToTextSizeMap[this.size];
    }
    getAvatarAppearance() {
        return this.appearance ? (this.appearance === 'dark' ? 'light' : 'dark') : undefined;
    }
    render() {
        return (h("cosmos-asset-item-base", { appearance: this.appearance, class: generateClassNames({ appearance: this.appearance, size: this.getSizeClasses() }), exportparts: "base: base, headline: name, meta-text-secondary: nationality, meta-text-primary: discipline, additional-info: additional-info", headline: this.name, href: this.href, metaTextPrimary: this.discipline, metaTextPrimaryClamp: true, metaTextSecondary: this.nationality, metaTextSecondaryClamp: true, metaTextAdditional: this.additionalInfo, rel: this.rel, size: this.size, target: this.target }, this.external && (h("span", { class: generateClassNames('external-icon-container'), slot: "headline-suffix" }, "\u200B", h("cosmos-icon-external-link", { class: generateClassNames('external-icon') }))), h("cosmos-avatar", { appearance: this.getAvatarAppearance(), class: generateClassNames('avatar'), framed: this.profileImageFramed, name: this.name, part: "profile-image", slot: "image", src: this.profileImage }), this.hasFlagSlot() && (h("span", { slot: "meta-text-secondary-prefix", class: generateClassNames('flag') }, h("slot", { name: "flag" }))), this.hasAdditionalInfoSlot() && (h("span", { slot: "meta-text-additional-info-prefix", class: generateClassNames('additional-info') }, h("slot", { name: "additional-info-prefix" })))));
    }
    get element() { return this; }
    static get style() { return CosmosPersonItemStyle0; }
}, [1, "cosmos-person-item", {
        "additionalInfo": [513, "additional-info"],
        "appearance": [513],
        "discipline": [513],
        "external": [516],
        "href": [513],
        "name": [513],
        "nationality": [513],
        "profileImage": [513, "profile-image"],
        "profileImageFramed": [516, "profile-image-framed"],
        "rel": [513],
        "size": [513],
        "target": [513]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["cosmos-person-item", "cosmos-asset-item-base", "cosmos-avatar", "cosmos-icon-external-link", "cosmos-image", "cosmos-text"];
    components.forEach(tagName => { switch (tagName) {
        case "cosmos-person-item":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, CosmosPersonItem$1);
            }
            break;
        case "cosmos-asset-item-base":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "cosmos-avatar":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "cosmos-icon-external-link":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "cosmos-image":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "cosmos-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const CosmosPersonItem = CosmosPersonItem$1;
const defineCustomElement = defineCustomElement$1;

export { CosmosPersonItem, defineCustomElement };
